import React, { useEffect, useMemo, useState } from "react";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";

import DialogManageUser from "@/app/models/dialogs/DialogMangeUser";
import UniInput from "@/app/components/common/actions/inputs-b/UniInput";
import { UniDropdown } from "@/app/components/common/actions/inputs-b/UniDropdown";
import { ICON_BASE_PATH } from "@/app/utils/config";

import "../ThirdParty.css";
import SubmitLoading from "@/app/components/webloading/errorHandling/SubmitLoading";
import { RequestConfirmation } from "@/app/components/common/alert/RequestConfirmation";
import { useAdminContext } from "@/app/services/context/AdminContext";
import { useThirdPartySubFeatureUpdate } from "@/app/hooks/useAdminApiHooks";
import { FIELD_ICONS } from "../ThirdPartyFieldIcons";
import { gxLookupTypeApi } from "@/app/apiConfig/services/bulkPaymentsPayrollApiLayer";
import RequiredMessage from "@/app/components/webloading/errorHandling/RequiredMessage";

// ============================================================
// Help Content
// ============================================================
const ZohoHelpContent = () => (
  <div className="gx-p14-m gx-help-content">
    <p>
      To connect your Zoho ERP account, you'll need a few credentials from your
      Zoho Developer Console:
    </p>
    <ol className="gx-help-list">
      <li>Log in to your Zoho Developer Console.</li>

      <li>
        Go to <strong>API Console → Client ID &amp; Secret</strong>.
        <ol>
          <li>
            Copy your <strong>Client ID</strong>.
          </li>
          <li>
            Copy your <strong>Client Secret</strong>.
          </li>
        </ol>
      </li>

      <li>
        Next, go to your <strong>Zoho Admin Panel</strong>.
        <ol>
          <li>
            Open your <strong>Profile Settings</strong>.
          </li>
          <li>
            Copy your <strong>Organization ID</strong>.
          </li>
          <li>
            Select your <strong>Region</strong> from the dropdown (must match
            your Zoho account's region).
          </li>
        </ol>
      </li>

      <li>
        Paste all the above values into the respective fields here and submit
        your request.
      </li>
    </ol>
  </div>
);

// ============================================================
// Field Configuration & Validation
// ============================================================

const FIELD_TOOLTIPS: Record<string, string> = {
  "Client Secret": "",
  "Organization ID":
    "Organization ID is a unique key for your Zoho account, found in the Admin Panel or under profile settings. Contact Zoho support if you're unable to locate it.",
  "Base URL":
    "Select the region that matches your Zoho account's data center. A mismatch may cause silent API failures.",
};

const MASKED_FIELD_NAMES = new Set(["Client Secret"]);

// Format rules per field: value must already be non-empty to reach these.
const FIELD_VALIDATION_RULES: Record<
  string,
  { validate: (val: string) => boolean; errorMessage: string }
> = {
  "Client ID": {
    validate: (val: string) => /^1000\.[A-Z0-9]+$/.test(val.trim()),
    errorMessage:
      'Client ID must start with "1000." followed by alphanumeric characters (e.g., 1000.0V5M2IXVLFFSJ6XFC1A3SVAEX21Q4A)',
  },
  "Organization ID": {
    validate: (val: string) => /^\d+$/.test(val.trim()),
    errorMessage: "Organization ID must be a numeric string",
  },
};

const getRequiredFieldMessage = (fieldName: string) =>
  `Please enter ${fieldName}`;

// Single source of truth for validating one field's value — required,
// field-specific format rule (FIELD_VALIDATION_RULES), and max length
// (response's fieldLength). Returns "" when the value is valid.
const validateField = (
  field: { fieldName: string; fieldLength: number },
  value: string,
): string => {
  if (!value.trim()) {
    return getRequiredFieldMessage(field.fieldName);
  }

  const rule = FIELD_VALIDATION_RULES[field.fieldName];
  if (rule && !rule.validate(value)) {
    return rule.errorMessage;
  }

  if (field.fieldLength > 0 && value.length > field.fieldLength) {
    return `${field.fieldName} must be at most ${field.fieldLength} characters`;
  }

  return "";
};

// ============================================================
// Component
// ============================================================

// NOTE: the lookup API returns gxLookupCode / gxLookupValue (previously this
// interface was declared with lookupCode/lookupValue, which don't exist on
// the response — every mapped option came out `undefined`, which is what
// crashed the Kendo DropDownList with "Cannot read properties of undefined
// (reading 'disabled')").
interface LookupTypeResponse {
  gxLookupId: number;
  gxLookupType: string;
  gxLookupCode: string;
  gxLookupValue: string;
  gxLookupDesc: string | null;
  gxLookupSeq: number;
}

// Shape consumed by UniDropdown for a two-line option: gxLookupCode shown as
// the main label, gxLookupValue shown as the smaller sub-label and used as
// the actual stored/selected value.
interface BaseUrlOption {
  label: string;
  subLabel: string;
  value: string;
}

const ZohoErp = () => {
  const navigate = useNavigate();

  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Which fields the user has actually interacted with (typed in or
  // blurred). Errors are only *surfaced* for touched fields — but once a
  // field is touched its error persists on every keystroke until the value
  // is genuinely valid.
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>(
    {},
  );
  const [visibleFields, setVisibleFields] = useState<Record<string, boolean>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [baseUrlOptions, setBaseUrlOptions] = useState<BaseUrlOption[]>([]);
  const [apiError, setApiError] = useState<{
    errorCode?: string | number | null;
    message?: string | null;
  }>({});

  const { selectedThirdPartySubFeature } = useAdminContext();
  const { mutate: updateThirdPartySubFeature } =
    useThirdPartySubFeatureUpdate();

  const recordState = selectedThirdPartySubFeature?.recordState;
  const isConnected = recordState === "A";
  const isReadOnly = isConnected;

  const fieldList =
    selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];

  // ========================================================
  // Fetch Base URL options on mount
  // ========================================================
  useEffect(() => {
    const fetchBaseUrlOptions = async () => {
      try {
        const response = await gxLookupTypeApi({
          gxLookupType: "ZOHO_BASE_URL",
        });
        if (response.status === 1 && Array.isArray(response?.data)) {
          const options = (response.data as LookupTypeResponse[]).map(
            (item) => ({
              label: item.gxLookupCode,
              subLabel: item.gxLookupValue,
              value: item.gxLookupValue,
            }),
          );
          setBaseUrlOptions(options);
        } else {
          setBaseUrlOptions([]);
        }
      } catch {
        setBaseUrlOptions([]);
      }
    };

    fetchBaseUrlOptions();
  }, []);

  // ========================================================
  // Seed field values from backend config whenever the selected
  // sub-feature (or the fetched Base URL options) changes.
  // ========================================================
  useEffect(() => {
    const list = selectedThirdPartySubFeature?.compConfigFeatureFeeList ?? [];
    const initial: Record<string, string> = {};

    list.forEach((field) => {
      let value = field.fieldValue ?? "";

      // If Base URL has no value and we have options, default to first option
      if (
        field.fieldName === "Base URL" &&
        !value &&
        baseUrlOptions.length > 0
      ) {
        value = baseUrlOptions[0].value;
      }

      initial[field.fieldName] = value;
    });

    setFieldValues(initial);
    setFieldErrors({});
    setTouchedFields({});
    setVisibleFields({});
    setApiError({});
  }, [selectedThirdPartySubFeature, baseUrlOptions]);

  // ========================================================
  // Live validation
  // ========================================================

  // Recomputes and stores the error for one field. Keeps fieldErrors in
  // sync with the current value so an error can never be "lost" between
  // keystrokes.
  const setErrorFor = (name: string, value: string) => {
    const field = fieldList.find((f) => f.fieldName === name);
    const error = field ? validateField(field, value) : "";

    setFieldErrors((prev) => {
      if (!error) {
        if (!prev[name]) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      }
      if (prev[name] === error) return prev;
      return { ...prev, [name]: error };
    });
  };

  // Updates a single field's value and re-validates it on every keystroke.
  // Typing marks the field as touched, so the message appears right away and
  // only clears once the required + format + max-length rules are satisfied.
  const handleFieldChange = (name: string, val: string) => {
    if (isReadOnly) return;
    setFieldValues((prev) => ({ ...prev, [name]: val }));
    setTouchedFields((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
    setErrorFor(name, val);
    // A new attempt invalidates the previous submit error.
    setApiError((prev) => (prev.errorCode || prev.message ? {} : prev));
  };

  // UniInput/UniDropdown call onValidate on blur — used as the "touched"
  // signal so tabbing through an empty or invalid field surfaces its
  // message without waiting for submit.
  const handleFieldValidate = (name: string) => {
    setTouchedFields((prev) => (prev[name] ? prev : { ...prev, [name]: true }));
    setErrorFor(name, fieldValues[name] ?? "");
  };

  const toggleFieldVisibility = (fieldName: string) => {
    setVisibleFields((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  // Checks every rendered field with the same validateField logic used live.
  const validateRequiredFields = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    fieldList.forEach((field) => {
      const value = fieldValues[field.fieldName] ?? "";
      const error = validateField(field, value);
      if (error) {
        errors[field.fieldName] = error;
      }
    });

    return errors;
  };

  // The errors actually shown on the inputs — only for touched fields.
  const visibleFieldErrors = useMemo(() => {
    const shown: Record<string, string> = {};
    Object.entries(fieldErrors).forEach(([name, message]) => {
      if (touchedFields[name] && message) shown[name] = message;
    });
    return shown;
  }, [fieldErrors, touchedFields]);

  // Detailed breakdown handed to RequiredMessage, in the same order the
  // fields are rendered so it reads top-to-bottom like the form.
  const errorDetails = useMemo(
    () =>
      fieldList
        .filter((field) => visibleFieldErrors[field.fieldName])
        .map((field) => ({
          fieldName: field.fieldName,
          message: visibleFieldErrors[field.fieldName],
        })),
    [fieldList, visibleFieldErrors],
  );

  const summaryMessage = apiError.errorCode
    ? null
    : apiError.message ||
      (errorDetails.length > 0
        ? "Please correct the highlighted fields before submitting."
        : null);

  // ========================================================
  // Submit handler
  // ========================================================

  const handleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    if (isReadOnly) return;

    if (!selectedThirdPartySubFeature) {
      console.error(
        "[ZohoErp] No selectedThirdPartySubFeature in context - cannot submit.",
      );
      return;
    }

    const errors = validateRequiredFields();
    if (Object.keys(errors).length > 0) {
      // Mark every field touched on submit so all messages surface, both
      // inline and in the RequiredMessage summary.
      setFieldErrors(errors);
      setTouchedFields(
        fieldList.reduce<Record<string, boolean>>((acc, field) => {
          acc[field.fieldName] = true;
          return acc;
        }, {}),
      );
      setApiError({});
      return;
    }
    setFieldErrors({});

    const updatedCompConfigFeatureFeeList = (
      selectedThirdPartySubFeature.compConfigFeatureFeeList ?? []
    ).map((field) => ({
      smeConfFeatureFeeId: field.smeConfFeatureFeeId,
      smeFeatureFeeId: field.smeFeatureFeeId,
      groupSmeCompId: selectedThirdPartySubFeature.groupSmeCompId,
      subFeatureId: field.subFeatureId,
      subFeatureConfigId: field.subFeatureConfigId,
      cifId: selectedThirdPartySubFeature.cifId ?? null,
      fieldName: field.fieldName,
      fieldValue: fieldValues[field.fieldName] ?? field.fieldValue,
      redirectUrl: field.redirectUrl,
    }));

    const payload = {
      subFeatureId: selectedThirdPartySubFeature.subFeatureId,
      groupSmeCompId: selectedThirdPartySubFeature.groupSmeCompId,
      compConfigFeatureFeeList: updatedCompConfigFeatureFeeList,
    };

    setIsLoading(true);
    setApiError({});

    try {
      updateThirdPartySubFeature(payload, {
        onSuccess: () => {
          setShowSubmitted(true);
        },
        onError: (error: any) => {
          const errorCode = error?.response?.data?.errorCode;
          const errorMessage =
            error?.response?.data?.message ||
            "Something went wrong. Please try again.";
          setApiError({
            errorCode: errorCode || null,
            message: errorMessage,
          });
        },
      });
    } catch {
      setApiError({
        errorCode: null,
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    navigate(-1);
  };

  const handleCloseSubmitted = () => {
    setShowSubmitted(false);
    navigate(-1);
  };

  // ========================================================
  // Render
  // ========================================================

  return (
    <div className="gx-erp-container">
      {isLoading && <SubmitLoading />}

      {showSubmitted && (
        <RequestConfirmation
          actionType="submitted"
          onConfirm={() => handleCloseSubmitted()}
          onClose={handleCloseSubmitted}
          title="Your Zoho ERP connection request has been submitted"
          message="Thank you for choosing GrowX. Our team will reach out to you at your registered contact number soon with further details regarding your request."
        />
      )}

      {showHelp && (
        <RequestConfirmation
          actionType="help"
          title="How to get this information"
          onConfirm={() => setShowHelp(false)}
          onClose={() => setShowHelp(false)}
          requiredContent={<ZohoHelpContent />}
        />
      )}

      <DialogManageUser
        isOpen={true}
        handleHeaderClose={handleCloseModal}
        handleFooterSubmit={handleSubmit}
        title={t("request-to-connect")}
        subtitle={t("zoho-erp")}
        submit={t("submit_request")}
        cancel=""
        userRoll=""
        userStatus=""
        handleAcitiveUsers={() => console.log("Activate users")}
        hideFooter={isConnected}
      >
        <div className="gx-connect-container">
          <div className="gx-connect-header">
            <div className="gx-connect-header-text">
              <h1 className="gx-p18-sb">{t("connect-zoho-erp")}</h1>
              <p className="gx-p12-m">
                Securely link your Zoho ERP account to synchronize your business
                data, manage integrations, and streamline your operations within
                the platform.
              </p>
            </div>

            <div className="gx-how-to-get" onClick={() => setShowHelp(true)}>
              <img
                src={`${ICON_BASE_PATH}interrogation-mark.svg`}
                alt="Info Icon"
              />
              <p className="gx-p14-sb">Help</p>
            </div>
          </div>

          <form className="gx-third-party-form" onSubmit={handleSubmit}>
            <div className="gx-third-party-form-grid">
              {fieldList.map((field) => {
                const commonProps = {
                  key: field.subFeatureConfigId,
                  fieldName: field.fieldName,
                  label: field.fieldName,
                  value: fieldValues[field.fieldName] ?? "",
                  onValidate: (name: string) => handleFieldValidate(name),
                  validationMessage: visibleFieldErrors[field.fieldName] ?? "",
                  tooltip: FIELD_TOOLTIPS[field.fieldName],
                  tooltipFloat: "down" as const,
                };

                if (field.fieldType === "DROPDOWN") {
                  const dropdownData =
                    field.fieldName === "Base URL" ? baseUrlOptions : [];

                  return (
                    <UniDropdown
                      {...commonProps}
                      onChange={(val) =>
                        handleFieldChange(field.fieldName, val)
                      }
                      data={dropdownData}
                      showStarLabel
                      iconSrc={FIELD_ICONS[field.fieldName] ?? "region.svg"}
                      minLength={3}
                      max={field.fieldLength > 0 ? field.fieldLength : 50}
                      disable={isReadOnly}
                    />
                  );
                }

                const isMasked = MASKED_FIELD_NAMES.has(field.fieldName);
                const isVisible = visibleFields[field.fieldName] ?? false;

                return (
                  <div
                    key={field.subFeatureConfigId}
                    className={isMasked ? "gx-masked-field-wrap" : undefined}
                    onDoubleClick={
                      isMasked
                        ? () => toggleFieldVisibility(field.fieldName)
                        : undefined
                    }
                  >
                    <UniInput
                      {...commonProps}
                      onChange={(val) =>
                        handleFieldChange(field.fieldName, val)
                      }
                      iconFile={FIELD_ICONS[field.fieldName]}
                      minLength={field.fieldName === "Client Secret" ? 20 : 1}
                      max={field.fieldLength > 0 ? field.fieldLength : 100}
                      disable={isReadOnly}
                      type={isMasked && !isVisible ? "password" : "text"}
                      placeholder={
                        field.fieldName === "Client ID"
                          ? "e.g., 1000.0V5M2IXVLFFSJ6XFC1A3SVAEX21Q4A"
                          : undefined
                      }
                    />
                  </div>
                );
              })}
            </div>

            <button
              type="submit"
              className="gx-hidden-submit"
              aria-hidden="true"
              tabIndex={-1}
            >
              Hidden Submit
            </button>
          </form>

          <div className="gx-zoho-required">
            <RequiredMessage
              errorCode={apiError.errorCode}
              message={summaryMessage}
              details={errorDetails}
            />
          </div>
        </div>
      </DialogManageUser>
    </div>
  );
};

export default ZohoErp;
